// AI-CONTEXT: See docs/features/update-aanvragen.md

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import sortUpdateRequests from '../../helpers/updateRequests/sortUpdateRequests'
import updateRequestListState from '../../helpers/updateRequests/updateRequestListState'
import UpdateRequestList from './UpdateRequestList'

/**
 * De updateverzoeken van deze organisatie, met de stand van zaken.
 *
 * Geen filter op wie het indiende: de hele organisatie ziet elkaars verzoeken,
 * zodat collega's niet twee keer hetzelfde aanvragen.
 *
 * De sortering gebeurt hier en niet in de query. Een Firestore-orderBy op
 * `Timestamp` laat een document zonder dat veld stil wegvallen, en dat is
 * precies het document waar iemand naar zoekt.
 * AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
 *
 * De fout van de listener wordt bewust opgevangen: een geweigerde lees geeft
 * een LEGE lijst, en 'je hebt nog niets aangevraagd' zou dan een onwaarheid
 * zijn. Ook op de eigen omgeving — een ontbrekende Console-regel ziet er
 * precies zo uit.
 * AI-CONTEXT: See docs/inbox.md Bug #133
 */
const MyUpdateRequests = () => {

  // State
  const [error, setError] = useState(/** @type {{code?: string}|null} */ (null))

  // Firestore
  const onError = useCallback((queryError) => { setError(queryError) }, [])
  const docs = useFirestoreQuery('UpdateRequests', { onError })

  // Een geslaagde snapshot wist een eerdere fout. Zonder dit blijft een
  // tijdelijke storing voor altijd staan en verdwijnt de lijst erachter —
  // updateRequestListState laat een fout immers winnen van de documenten.
  // Bij een mislukte listener roept de engine setDocs niet aan, dus `docs`
  // houdt zijn identiteit en dit effect draait juist NIET.
  useEffect(() => { if (Array.isArray(docs)) setError(null) }, [docs])

  const lijst = useMemo(() => sortUpdateRequests(docs), [docs])
  const toestand = updateRequestListState({ docs, error })

  // JSX
  if (toestand === 'denied') {
    return (
      <p className="updates-empty">
        We kunnen je updateverzoeken nu niet tonen — deze omgeving heeft er geen
        toegang toe. Neem contact op met Deccos via info@deccos.nl.
      </p>
    )
  }

  if (toestand === 'failed') {
    return (
      <p className="updates-empty">
        We konden je updateverzoeken niet ophalen. Probeer het later opnieuw.
      </p>
    )
  }

  if (toestand === 'loading') return null

  if (toestand === 'empty') {
    return <p className="updates-empty">Je hebt nog geen updates aangevraagd.</p>
  }

  return <UpdateRequestList requests={lijst} />
}

export default MyUpdateRequests
